import { Injectable } from '@angular/core';
import { ProjectContactDirectoryDaoService } from '../project-contact-directory-dao.service';
import { ProjectContactDirectory } from 'src/app/_model/project-contact-directory';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { IMSId } from 'src/app/_model/ims-object';
import { ProjectContactDirectoryDbTableDefinition } from './project-contact-directory-db-table-definition';
import { LocalProjectContactDirectoryDbTableDefinition } from './local-project-contact-directory-db-table-definition';

@Injectable()
export class ProjectContactDirectoryDbDaoService implements ProjectContactDirectoryDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private projectContactDirectoryTableDefinition: ProjectContactDirectoryDbTableDefinition,
    private localProjectContactDirectoryTableDefinition: LocalProjectContactDirectoryDbTableDefinition,
  ) {}

  public async saveProjectContactDirectory(entries: ProjectContactDirectory[]): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const queries: Query[] = entries.map((entry) => ({
        statement:
          'INSERT OR REPLACE INTO ' +
          this.projectContactDirectoryTableDefinition.tableName +
          '(id, sourcetype, label, branchid, personid, functiongroupid, servicejobid,' +
          ' sortorder, functiongroupsortorder, notes, createdtstamp, updatedtstamp, personlabel, personphone, personemail, employeeid, databag)' +
          ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
        parameters: [
          Number(entry.getImsId()),
          entry.sourcetype,
          entry.label,
          entry.branchid,
          entry.personid,
          entry.functiongroupid,
          entry.servicejobid,
          entry.sortorder,
          entry.functiongroupsortorder,
          entry.notes,
          entry.createdtstamp,
          entry.updatedtstamp,
          entry.personlabel,
          entry.personphone,
          entry.personemail,
          entry.employeeid,
          entry.databag ? JSON.stringify(entry.databag) : undefined,
        ],
      }));
      await this.dbPersistenceService.executeBatch(db, queries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error saving project contact directory to db',
        error?.message,
      );
    }
  }

  public async createProjectContactDirectory(entry: ProjectContactDirectory): Promise<ProjectContactDirectory> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const query: Query = {
        statement:
          'INSERT INTO ' +
          this.localProjectContactDirectoryTableDefinition.tableName +
          '(sourcetype, label, branchid, personid, functiongroupid, servicejobid,' +
          ' sortorder, notes, createdtstamp, updatedtstamp, personlabel, personphone, personemail, employeeid, databag)' +
          ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?)',
        parameters: [
          entry.sourcetype,
          entry.label,
          entry.branchid,
          entry.personid,
          entry.functiongroupid,
          entry.servicejobid,
          entry.sortorder,
          entry.notes,
          entry.createdtstamp,
          entry.updatedtstamp,
          entry.personlabel,
          entry.personphone,
          entry.personemail,
          entry.employeeid,
          entry.databag ? JSON.stringify(entry.databag) : undefined,
        ],
      };

      await this.dbPersistenceService.executeStatement(db, query);
      const res = await this.dbPersistenceService.executeQuery(db, {
        statement: 'select last_insert_rowid() as id',
        parameters: [],
      });
      if (res && res.values) {
        entry.id.secondaryId = res.values.shift()?.id;
      }
      return entry;
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error creating local project contact directory entry',
        error?.message,
      );
      return undefined;
    }
  }

  public async deleteLocalProjectContactDirectory(id: IMSId): Promise<void> {
    const db = await this.dbPersistenceService.getImsDb();
    try {
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' +
          this.localProjectContactDirectoryTableDefinition.tableName +
          ' WHERE id = (?);',
        parameters: [id.secondaryId],
      });
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error deleting local project contact directory entry',
        id,
        error?.message,
      );
    }
  }

  /**
   * Drops a synced entry from the local copy of the contact directory. Only
   * used when the entry was removed in a form: the removal travels to the
   * backend with that form, and the backend is the one that decides - a sync
   * truncates this table and reloads it, so an entry the backend kept comes
   * right back.
   */
  public async deleteSyncedProjectContactDirectory(id: IMSId): Promise<void> {
    const db = await this.dbPersistenceService.getImsDb();
    try {
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' +
          this.projectContactDirectoryTableDefinition.tableName +
          ' WHERE id = (?);',
        parameters: [id.imsId],
      });
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error deleting synced project contact directory entry',
        id,
        error?.message,
      );
    }
  }

  public async getProjectContactDirectory(): Promise<ProjectContactDirectory[]> {
    const db = await this.dbPersistenceService.getImsDb();
    const entries: ProjectContactDirectory[] = [];

    // Synced entries - isolated try/catch so a problem reading the local table
    // (e.g. not migrated yet) can never wipe out the already-loaded synced ones.
    try {
      const data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.projectContactDirectoryTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        const row = data.values[i];
        entries.push(
          ProjectContactDirectory.fromDto(
            {
              id: row['id'],
              sourcetype: row['sourcetype'],
              label: row['label'],
              branchid: row['branchid'],
              personid: row['personid'],
              functiongroupid: row['functiongroupid'],
              servicejobid: row['servicejobid'],
              sortorder: row['sortorder'],
              functiongroupsortorder: row['functiongroupsortorder'],
              notes: row['notes'],
              createdtstamp: row['createdtstamp'],
              updatedtstamp: row['updatedtstamp'],
              personlabel: row['personlabel'],
              personphone: row['personphone'],
              personemail: row['personemail'],
              employeeid: row['employeeid'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading synced project contact directory from db',
        error?.message,
      );
    }

    // Locally created entries (not yet on the server, no real imsId)
    try {
      const localData = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' + this.localProjectContactDirectoryTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < localData.values.length; i++) {
        const row = localData.values[i];
        entries.push(
          ProjectContactDirectory.fromDto(
            {
              id: undefined,
              secondaryid: row['id'],
              sourcetype: row['sourcetype'],
              label: row['label'],
              branchid: row['branchid'],
              personid: row['personid'],
              functiongroupid: row['functiongroupid'],
              servicejobid: row['servicejobid'],
              sortorder: row['sortorder'],
              notes: row['notes'],
              createdtstamp: row['createdtstamp'],
              updatedtstamp: row['updatedtstamp'],
              personlabel: row['personlabel'],
              personphone: row['personphone'],
              personemail: row['personemail'],
              employeeid: row['employeeid'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading local project contact directory from db',
        error?.message,
      );
    }

    return entries;
  }

  public async reset(): Promise<void> {
    // Unlike clear() (used during a sync), a Reset should wipe locally
    // created, not-yet-synced entries too.
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localProjectContactDirectoryTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localProjectContactDirectoryTableDefinition.tableName,
    );
  }

  public async clear(): Promise<void> {
    // Only the synced table is cleared here - locally created entries (in the
    // local table) are not pushed to the backend yet, so they must survive a
    // resync. See LocalProjectContactDirectoryDbTableDefinition.
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.projectContactDirectoryTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.projectContactDirectoryTableDefinition.tableName,
    );
  }
}
